<script setup lang="ts">
import { ref, onBeforeMount, onMounted, computed, watch } from 'vue';
import DataTable from '@/components/DataTable/DataTable.vue';
import Column from '@/components/DataTable/DataTableColumn.vue';
import { usePortfolioStore } from '@/stores/portfolio';
import { useUnderwritingStore } from '@/stores/underwriting';
import { useGlobalStore } from '@/stores/global';
import moment from 'moment';
import { api } from '@/services/api';
import axios from 'axios';
import Tooltip from '@/components/TooltipComponent.vue';
import Dropdown from '@/components/DropdownComponent.vue';
import CheckboxList from '@/components/CheckboxListComponent.vue';
import SelectComponent from '@/components/SelectComponent.vue';
import ArrowDownIcon from '@/assets/svg/ArrowDownIcon.vue';
import generateRandomKey from '@/utilities/generateRandomKey';
import FilterIcon from '@/assets/svg/FilterIcon.vue';
import OptionComponent from '@/components/OptionComponent.vue';
import InputTextComponent from '@/components/InputTextComponent.vue';
import JetButton from '@/components/JetButton.vue';
import PlusIcon from '@/assets/svg/PlusIcon.vue';
import DownloadIcon from '@/assets/svg/DownloadIcon.vue';
import EditPenIcon from '@/assets/svg/EditPenIcon.vue';
import FirstPageIcon from '@/assets/svg/FirstPageIcon.vue';
import PreviousPageIcon from '@/assets/svg/PreviousPageIcon.vue';
import NextPageIcon from '@/assets/svg/NextPageIcon.vue';
import LastPageIcon from '@/assets/svg/LastPageIcon.vue';
import LockIcon from '@/assets/svg/LockIcon.vue';
import UnlockIcon from '@/assets/svg/UnlockIcon.vue';
import filterValueSorting from '@/utilities/filterValueSorting';

// const treeRef = ref<InstanceType<typeof ElTree>>();

const columnLocksFooter = ref('');
function toggleLock(key: number) {
  if (columnLocks.value[key] == '') {
    columnLocks.value[key] = 'sticky';
  } else {
    columnLocks.value[key] = '';
  }

  let currOffset = 0;
  for (const i of underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel)) {
    if (columnLocks.value[i] != '') {
      columnLocksOffset.value[i] = currOffset + 56 + 'px';
      currOffset += 160;
    } else {
      columnLocksOffset.value[i] = null;
    }
  }

  columnLocksFooter.value = Object.values(columnLocks.value).join('').length > 0 ? 'sticky' : '';
}

const portfolioStore = usePortfolioStore();
const underwritingStore = useUnderwritingStore();
const globalStore = useGlobalStore();

const colShow = ref(999);

const rowCol = ref(0);
const currentMonth = computed(() => moment(portfolioStore.selectedBounceDate, 'YYYYMM').format('MMM-YYYY'));
const cuurentMonthIndex = ref(portfolioStore.all_uw_dates['month'].indexOf(currentMonth.value));
underwritingStore.setHierarchyOrder();

const tableData = computed(() => underwritingStore.tableData);
const tableDataColumns = computed(() => underwritingStore.tableDataColumns);

interface TotalTableDataType {
  value_array: number[];
}
const totalTableData = computed<TotalTableDataType>(() => underwritingStore.totalTableData);

const valueArrayColumns = computed(() => underwritingStore.valueArrayColumns);
const filterTree = ref(
  underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel).map((value) =>
    Object.keys(portfolioStore.dictionary[portfolioStore.parameters.hierarchies[value]]).map((x) => ({
      name: portfolioStore.dictionary[portfolioStore.parameters.hierarchies[value]][x],
      id: x,
      is_checked: true,
    }))
  )
);

const allFilterTreeKeys = ref(
  underwritingStore.hierarchyOrder
    .slice(0, underwritingStore.currentLevel)
    .map((value) => Object.keys(portfolioStore.dictionary[portfolioStore.parameters.hierarchies[value]]))
);
const checkedFilterTreeKeys = ref(underwritingStore.checkedFilterTreeKeys || structuredClone(allFilterTreeKeys.value));
function generateFilterTree(index) {
  const res: string[] = [];
  for (const i of filterTree.value[index]) {
    if (i.is_checked) {
      res.push(i.id);
    }
  }
  return res;
}

async function resetFilterTree(index: number) {
  for (const i of filterTree.value[index]) {
    i.is_checked = true;
  }

  checkedFilterTreeKeys.value[index] = generateFilterTree(index);
  // checkedFilterTreeKeys.value[index] = structuredClone(allFilterTreeKeys.value[index]);
  // hdropdowns.value[index].handleClose();
  await underwritingStore.setCheckedFilterTreeKeys(checkedFilterTreeKeys.value);
  underwritingStore.setIsFiltered(
    valueFiltersLength.value.reduce((s, a) => s + a, 0) +
      allFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0) -
      checkedFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0)
  );
}

async function applyFilterTree(index: number) {
  checkedFilterTreeKeys.value[index] = generateFilterTree(index);
  // checkedFilterTreeKeys.value[index] = treeRef.value[index]!.getCheckedKeys(true);
  // hdropdowns.value[index].handleClose();

  await underwritingStore.setCheckedFilterTreeKeys(checkedFilterTreeKeys.value);
  underwritingStore.setIsFiltered(
    valueFiltersLength.value.reduce((s, a) => s + a, 0) +
      allFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0) -
      checkedFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0)
  );
}

const selectedMonthOperation = ref('<=');

const amountType = computed(() => [
  ...[underwritingStore.accident_underwriting == 0 ? 'GEP' : 'GWP'],
  ...['Commission'],
  ...portfolioStore.parameters.line_size.slice(1).map((e) => {
    return e;
  }),
  ...['CCR A-priori', 'NLR A-priori', 'GLR A-priori', 'Attritional A-priori'],
  ...portfolioStore.parameters.claims_nature.slice(1).map((e) => {
    return toTitleCase(e) + ' Load';
  }),
  ...['Rate Change', 'Indexation', 'Elasticity', 'Manual Perturbation', 'Algorithmic Perturbation'],
]);
const currentAmountType = computed(() => underwritingStore.currentAmountType);
const currentFilterAmountType = ref(underwritingStore.currentAmountType);
const filterNumber = ref('');
const valueFilters = ref(underwritingStore.valueFilters || portfolioStore.all_uw_dates['month'].map(() => []));
const valueFiltersLength = ref(valueFilters.value.map((x) => x.reduce((s, a) => s + a, 0)));
const valueVisibleFilters = ref<string[][]>([]);
const isFiltered = computed(() => underwritingStore.isFiltered);

const isExportingTable = ref(false);

async function openDownloadDialog() {
  if (isExportingTable.value == false) {
    isExportingTable.value = true;

    let hierarchiesOrder = [];
    for (const i of underwritingStore.hierarchyOrder) {
      hierarchiesOrder.push(portfolioStore.parameters['hierarchies'][i]);
    }

    await api
      .post(import.meta.env.VITE_API_URL + 'underwriting/export-table', {
        bounce_id: portfolioStore.selectedBounceID,
        accident_underwriting: underwritingStore.accident_underwriting,
        selected_hierarchies: hierarchiesOrder.slice(0, underwritingStore.currentLevel),
        currency:
          'Amounts in ' + globalStore.currency.currencyFormat.currency + gwpUnits.value[selectedGWPUnit.value][1],
        precision: globalStore.currency.currencyFormat.precision,
        unit: gwpUnits.value[selectedGWPUnit.value][0],
        checkedFilterTreeKeys: checkedFilterTreeKeys.value,
        all_months: portfolioStore.all_uw_dates['month'],
        valueFilters: valueFilters.value || portfolioStore.all_uw_dates['month'].map(() => []),
      })
      .then(async (res) => {
        return axios({
          url: res.data.data.signed_blob,
          method: 'GET',
          responseType: 'blob',
        });
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'filename.xlsx'); // Set a filename for the downloaded file
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        isExportingTable.value = false;
      })
      .catch(() => {
        console.log('error');
        isExportingTable.value = false;
      });
  }
}

const SELECTED_MONTH_OPERATION_MAPPED = {
  '<=': 'Less than or equal to',
  '>=': 'Greater than or equal to',
  '=': 'Equal to',
  '<': 'Less than',
  '>': 'Greater than',
};

function calculateAmount(row) {
  if (!row || !valueArrayColumns.value) {
    return 0;
  }
  if (currentAmountType.value == 'CCR A-priori') {
    let ccr = row[valueArrayColumns.value['Attritional A-priori']];
    for (let i of portfolioStore.parameters.claims_nature.slice(1)) {
      ccr += row[valueArrayColumns.value[toTitleCase(i) + ' Load']];
    }
    ccr += row[valueArrayColumns.value['Commission']];
    return formatNumber(ccr);
  } else if (currentAmountType.value == 'NLR A-priori') {
    let nlr = row[valueArrayColumns.value['Attritional A-priori']];
    for (let i of portfolioStore.parameters.claims_nature.slice(1)) {
      nlr += row[valueArrayColumns.value[toTitleCase(i) + ' Load']];
    }
    nlr = nlr / (1 - row[valueArrayColumns.value['Commission']]);
    return formatNumber(nlr);
  } else if (currentAmountType.value == 'GLR A-priori') {
    let glr = row[valueArrayColumns.value['Attritional A-priori']];
    for (let i of portfolioStore.parameters.claims_nature.slice(1)) {
      glr += row[valueArrayColumns.value[toTitleCase(i) + ' Load']];
    }
    return formatNumber(glr);
  }

  return formatNumber(row[valueArrayColumns.value[currentAmountType.value]]);
}

function selectAmount(amount: string) {
  underwritingStore.setCurrentAmountType(amount);
}

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

const columnLocks = ref(
  Object.values(underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel)).reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {})
);

const columnLocksOffset = ref(
  Object.values(underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel)).reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {})
);
const selectedGWPUnit = computed<string>(() => globalStore.currency.currencyFormat.selectedGWPUnit);
const gwpUnits = computed(() => globalStore.currency.GWPUnits);

const windowHeight = ref(0);

function changePage(e) {
  underwritingStore.updatePageUnderwritingTable(e);
}

function onUnderwritingControllerModal(date, row, dateIdx) {
  let filters: { [key: string]: unknown[] }[] = [];
  if (row) {
    for (const i of underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel)) {
      const hierarchy = portfolioStore.parameters.hierarchies[i];

      let obj: { [key: string]: unknown[] } = {};

      obj[hierarchy] = [row[tableDataColumns.value[hierarchy]]];

      filters.push(obj);
    }
  } else {
    for (const i of underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel)) {
      const hierarchy = portfolioStore.parameters.hierarchies[i];

      let obj: { [key: string]: unknown[] } = {};
      obj[hierarchy] = checkedFilterTreeKeys.value[underwritingStore.hierarchyOrder.indexOf(i)];

      filters.push(obj);
    }
  }

  const a = {
    filters: filters,
    underwriting_month: moment(date, 'MMM-YYYY').format('YYYY-MM-01'),
    index: dateIdx,
  };
  underwritingStore.setUnderwritingControllerDate(a);
  underwritingStore.updateIsUnderwritingControllerModal(true);
}

onBeforeMount(() => {
  windowHeight.value = window.innerHeight - 40;
});

function formatNumber(x: number) {
  if (['GWP', 'GEP'].includes(currentAmountType.value)) {
    return numberWithCommas(x, false);
  } else if (currentAmountType.value == 'Elasticity') {
    return x.toFixed(1);
  }
  return (x * 100).toFixed(1) + '%';
}

function numberWithCommas(x: number, isValue: boolean) {
  if (!isValue) {
    return (x / parseFloat(gwpUnits.value[selectedGWPUnit.value][0]))
      .toFixed(globalStore.currency.currencyFormat.precision)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  } else {
    return (
      parseFloat(
        (x / parseFloat(gwpUnits.value[selectedGWPUnit.value][0])).toFixed(
          globalStore.currency.currencyFormat.precision
        )
      ) || ''
    );
  }
}

function addAmountToFilter() {
  valueVisibleFilters.value.push([currentFilterAmountType.value, selectedMonthOperation.value, filterNumber.value]);
  filterNumber.value = '';
}

// function initializeFilter(index: number) {
//   dropDownRender.value[index] = true;
//   filterNumber.value = '';
//   currentFilterAmountType.value = underwritingStore.accident_underwriting == 0 ? 'GEP' : 'GWP';
//   valueVisibleFilters.value = structuredClone(valueFilters.value[index]);
// }

async function applyValueFilter(index: number) {
  valueFilters.value[index] = structuredClone(valueVisibleFilters.value);
  valueFiltersLength.value[index] = valueFilters.value[index].reduce((x, a) => x + a, 0);
  // dropdowns.value[index].handleClose();
  await underwritingStore.setValueFilters(valueFilters.value);
  underwritingStore.setIsFiltered(
    valueFiltersLength.value.reduce((s, a) => s + a, 0) +
      allFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0) -
      checkedFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0)
  );
}

async function resetValueFilter(index: number) {
  valueFilters.value[index] = [];
  valueFiltersLength.value[index] = 0;
  // dropdowns.value[index].handleClose();
  await underwritingStore.setValueFilters(valueFilters.value);
  underwritingStore.setIsFiltered(
    valueFiltersLength.value.reduce((s, a) => s + a, 0) +
      allFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0) -
      checkedFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0)
  );
}

async function resetAllFilters() {
  for (let index in valueFilters.value) {
    valueFilters.value[index] = [];
    valueFiltersLength.value[index] = 0;
  }
  for (let index in checkedFilterTreeKeys.value) {
    checkedFilterTreeKeys.value[index] = structuredClone(allFilterTreeKeys.value[index]);
  }
  await underwritingStore.setValueFilters(valueFilters.value);
  await underwritingStore.setCheckedFilterTreeKeys(checkedFilterTreeKeys.value);
  underwritingStore.setIsFiltered(
    valueFiltersLength.value.reduce((s, a) => s + a, 0) +
      allFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0) -
      checkedFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0)
  );
}
function amountFormat(value) {
  return ['GWP', 'GEP'].includes(value[0])
    ? value[0] + ' ' + value[1] + ' ' + value[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : value[0] +
        ' ' +
        value[1] +
        ' ' +
        parseFloat(value[2]).toFixed(1) +
        (currentFilterAmountType.value == 'Elasticity' ? '' : '%');
}

onMounted(async () => {
  colShow.value = 999; //Math.ceil(window.innerWidth/100)*2
  filterFiterTreeWithCheckedFilterTree();

  await underwritingStore.fetchUnderwritingTable();
  sortFilterTree();
});

function sortFilterTree() {
  // Sort each hierarchy level separately
  for (let i = 0; i < filterTree.value.length; i++) {
    const hierarchyName = portfolioStore.parameters.hierarchies[underwritingStore.hierarchyOrder[i]];
    const groupValues = filterTree.value[i].map((item) => item.name);
    const sortedGroupValues = filterValueSorting(hierarchyName, groupValues);

    // Reorder filterTree items based on sorted values
    const sortedItems = sortedGroupValues
      .map((sortedName) => filterTree.value[i].find((item) => item.name === sortedName))
      .filter((item) => item !== undefined) as { name: any; id: string; is_checked: boolean }[];

    filterTree.value[i] = sortedItems;
  }
}

function filterInitialState() {
  allFilterTreeKeys.value = underwritingStore.hierarchyOrder
    .slice(0, underwritingStore.currentLevel)
    .map((value) => Object.keys(portfolioStore.dictionary[portfolioStore.parameters.hierarchies[value]]));
  checkedFilterTreeKeys.value = structuredClone(allFilterTreeKeys.value);
  columnLocks.value = Object.values(underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel)).reduce(
    (acc, key) => {
      acc[key] = '';
      return acc;
    },
    {}
  );
  filterTree.value = underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel).map((value) =>
    Object.keys(portfolioStore.dictionary[portfolioStore.parameters.hierarchies[value]]).map((x) => ({
      name: portfolioStore.dictionary[portfolioStore.parameters.hierarchies[value]][x],
      id: x,
      is_checked: true,
    }))
  );

  sortFilterTree();

  underwritingStore.setIsFiltered(
    valueFiltersLength.value.reduce((s, a) => s + a, 0) +
      allFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0) -
      checkedFilterTreeKeys.value.map((x) => x.length).reduce((x, a) => x + a, 0)
  );

  underwritingStore.setCheckedFilterTreeKeys(checkedFilterTreeKeys.value);
}

function filterFiterTreeWithCheckedFilterTree() {
  for (let i = 0; i < filterTree.value.length; i++) {
    for (let j = 0; j < filterTree.value[i].length; j++) {
      if (checkedFilterTreeKeys.value[i].includes(filterTree.value[i][j].id)) {
        filterTree.value[i][j].is_checked = true;
      } else {
        filterTree.value[i][j].is_checked = false;
      }
    }
  }
}

watch(
  () => underwritingStore.hierarchyOrder,
  async () => {
    filterInitialState();
  }
);

watch(
  () => portfolioStore.selectedLineSize,
  async () => {
    await underwritingStore.fetchUnderwritingTable();
  }
);

function removeAmountFromFilter(index: number) {
  valueVisibleFilters.value.splice(index, 1);
}

const contextMenuTable = ref();

function handleRightClickContextMenu(event, type, node) {
  rowCol.value = type;
  contextMenuTable.value.showMenu(event, node);
}

function checkIfAllFilterIsChecked(index: number) {
  return filterTree.value[index].every((x) => x.is_checked);
}
</script>

<template>
  <div>
    <div class="flex justify-between flex-row flex-wrap px-5 py-3 bg-white -mb-3">
      <div class="mr-3 mt-1 relative">
        <Dropdown>
          <template #menu>
            <span class="text-lg">
              {{ currentAmountType }} by
              <span :class="'text-sybil-teal hover:text-green-700 '"
                ><b>{{ underwritingStore.accident_underwriting == 1 ? 'Underwriting' : 'Accident' }}</b></span
              >
              Month
            </span>
          </template>
          <template #dropdown>
            <div class="bg-white shadow-md h-96 overflow-scroll">
              <div class="px-5 py-3">
                <div v-for="(amount, index) in amountType" :key="generateRandomKey(index)">
                  <div
                    class="flex flex-row items-center pl-8 px-2 py-1 cursor-pointer hover:text-sybil-teal hover:bg-sybil-light-teal-2"
                    @click="selectAmount(amount)"
                  >
                    {{ amount }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Dropdown>
      </div>
    </div>
  </div>
  <div style="position: relative">
    <div class="w-full h-full overflow-auto" style="max-height: calc(100vh - 300px)">
      <DataTable v-if="tableData && tableDataColumns" :data="tableData">
        <Column
          header-style="background-color:#bbe2d3 ;text-align:center;"
          style="width: 32px; text-align: center; left: 0px; position: sticky"
          body-style="background-color: #eef8f4;"
          header-class="px-3 py-2 z-20"
          footer-class="bottom-0 z-30"
          body-class="z-10"
          :field="tableDataColumns['Index']"
          :footer-style="{
            'background-color': isFiltered == 0 ? '#bbe2d3' : '#f3d19e',
          }"
        >
          <template #header>
            <div style="width: 32px" class="w-full text-center font-bold whitespace-nowrap">
              <Tooltip content="Change hierarchy" placement="bottom" :show-after="1000">
                <img
                  src="@/views/Underwriting/assets/Hierarchy.svg"
                  data-testid="change-hierarchy"
                  width="20"
                  class="png-teal cursor-pointer ml-1"
                  @click="underwritingStore.updateChangeHierarchyOrderModal(true)"
                />
              </Tooltip>
            </div>
          </template>
          <template #body="slotProps">
            <p class="text-xs px-3 py-2">{{ slotProps.value }}</p>
          </template>
          <template #footer>
            <p class="text-xs px-3 py-2">
              {{ isFiltered == 0 ? 'Overall' : 'Subtotal' }}
            </p>
          </template>
        </Column>
        <Column
          v-for="(value, index) in underwritingStore.hierarchyOrder.slice(0, underwritingStore.currentLevel)"
          :key="generateRandomKey(index)"
          header-style="background-color:#bbe2d3; color: rgb(107,114,128);"
          style="width: 160px; text-align: center; height: 20px"
          header-class="px-3 py-2"
          body-style="background-color: #eef8f4;"
          :field="tableDataColumns[portfolioStore.parameters.hierarchies[value]]"
          :footer-style="{ 'background-color': isFiltered == 0 ? '#bbe2d3' : '#f3d19e' }"
          :class="columnLocks[value]"
          :style="{ left: columnLocksOffset[value], 'z-index': columnLocks[value] == '' ? 0 : 10 }"
        >
          <template #header>
            <div
              class="font-bold text-sm text-center w-full relative whitespace-nowrap flex flex-row"
              style="height: 20px; width: 160px"
            >
              <span
                class="cursor-pointer hover:text-sybil-teal text-lg absolute top-0 -left-1 h-4 w-4"
                @click="toggleLock(value)"
              >
                <UnlockIcon v-if="columnLocks[value] == ''" />
                <LockIcon v-else />
              </span>

              <p data-testid="hierarchy-order" class="ml-4">
                {{ portfolioStore.parameters.hierarchies[value] }}
              </p>
              <Dropdown>
                <template #menu="{ isDropdownOpen }">
                  <ArrowDownIcon
                    v-if="checkIfAllFilterIsChecked(index)"
                    class="dropdown-button hover:text-sybil-teal"
                    :class="{ open: isDropdownOpen }"
                  />
                  <FilterIcon
                    v-if="!checkIfAllFilterIsChecked(index)"
                    class="dropdown-button hover:text-sybil-teal"
                    :class="{ open: isDropdownOpen }"
                  />
                </template>
                <template #dropdown>
                  <div class="bg-white shadow-md" style="width: 350px; height: 420px">
                    <div class="px-5 mt-3 text-sybil-charcoal w-full text-center text-lg">
                      {{ portfolioStore.parameters.hierarchies[value] }}
                    </div>
                    <div class="p-4">
                      <CheckboxList
                        class="overflow-y-scroll border-2 border-gray-100"
                        style="height: 305px"
                        :data="filterTree[index]"
                      />
                    </div>
                    <div class="items-center flex justify-end pr-3 pb-6">
                      <JetButton @click="resetFilterTree(index)">Reset</JetButton>
                      <JetButton class="ml-3" @click="applyFilterTree(index)">Apply</JetButton>
                    </div>
                  </div>
                </template>
              </Dropdown>
            </div>
          </template>
          <template #body="slotProps">
            <p class="text-xs text-left px-5 mt-1 text-sybil-charcoal">
              {{ portfolioStore.dictionary[portfolioStore.parameters.hierarchies[value]][slotProps.value] }}
            </p>
          </template>
        </Column>
        <Column
          v-for="(value, index) in portfolioStore.all_uw_dates['month'].slice(0, colShow)"
          :key="generateRandomKey(index)"
          :field="tableDataColumns[value]"
          style="width: 180px"
          body-style="background-color: #ffffff;"
          header-style="background-color:#bbe2d3;"
          :footer-style="{ 'background-color': isFiltered == 0 ? '#bbe2d3' : '#f3d19e' }"
        >
          <template #header>
            <div
              class="whitespace-nowrap cursor-context-menu"
              :class="index > cuurentMonthIndex ? 'text-blue-500' : ''"
              style="width: 140px; height: 20px; font-size: 0.8rem; padding-top: 2px"
              @contextmenu.prevent.stop="handleRightClickContextMenu($event, 1, [value, index])"
            >
              <p>
                <span data-testid="underwriting-month">
                  {{ value }}
                </span>
                <Dropdown>
                  <template #menu="{ isDropdownOpen }">
                    <ArrowDownIcon class="dropdown-button hover:text-sybil-teal" :class="{ open: isDropdownOpen }" />
                  </template>
                  <template #dropdown>
                    <div class="bg-white shadow-md w-64" style="height: 530px">
                      <div class="px-5 mt-1 text-sybil-charcoal w-full text-center text-lg pb-4 pt-4">
                        {{ value }}
                      </div>
                      <div class="dropdown-container px-5 py-2 w-74" style="height: 300px">
                        <div class="mr-3 w-full">
                          <SelectComponent v-model="currentFilterAmountType" class="w-full">
                            <OptionComponent
                              v-for="(amount, indexAmount) in amountType"
                              :key="generateRandomKey(indexAmount)"
                              :data-testid="'underwriting-amount-filter-option-' + value"
                              :label="amount"
                              :value="amount"
                            />
                          </SelectComponent>
                          <SelectComponent
                            v-model="selectedMonthOperation"
                            class="text-center w-full"
                            :label="SELECTED_MONTH_OPERATION_MAPPED[selectedMonthOperation]"
                          >
                            <OptionComponent
                              :data-testid="'less-than-equal-selection-' + value"
                              label="Less than or equal to"
                              value="<="
                            />
                            <OptionComponent label="Less than" value="<" />
                            <OptionComponent label="Greater than or equal to" value=">=" />
                            <OptionComponent label="Greater than" value=">" />
                            <OptionComponent label="Equal to" value="=" />
                          </SelectComponent>
                          <div class="flex flex-row">
                            <InputTextComponent
                              v-model="filterNumber"
                              data-testid="underwriting-amount-filter-input"
                              style="height: 32px; width: 167px"
                              placeholder="Enter amount"
                            >
                              <template #side>
                                <span
                                  class="inline-flex flex-none items-center justify-center w-12 h-8 px-2 text-center border border-gray-400 bg-gray-100 text-sm"
                                  >{{
                                    ['GEP', 'GWP'].includes(currentFilterAmountType)
                                      ? globalStore.currency.currencyFormat.currency
                                      : '%'
                                  }}</span
                                >
                              </template>
                            </InputTextComponent>
                          </div>
                          <JetButton
                            class="mt-3"
                            :disabled="filterNumber == ''"
                            data-testid="add-amount-to-filter"
                            @click="addAmountToFilter()"
                            >Add</JetButton
                          >
                          <div
                            class="p-3 text-sybil-charcoal text-xs h-56 border-2 border-gray-100 shadow-internal my-3"
                          >
                            <div>Filters to apply...</div>
                            <div
                              v-for="(valueVisibleFilter, indexFilter) in valueVisibleFilters"
                              :key="indexFilter + '-filters'"
                              class="ml-2 w-full flex justify-start items-center pr-3"
                            >
                              {{ amountFormat(valueVisibleFilter) }}
                              <span
                                class="text-red-500 cursor-pointer ml-auto"
                                @click="removeAmountFromFilter(indexFilter)"
                                >remove</span
                              >
                            </div>
                          </div>
                          <div>
                            <JetButton
                              :disabled="valueFilters[index] == valueVisibleFilters"
                              data-testid="apply-month-filter"
                              @click="applyValueFilter(index)"
                              >Apply</JetButton
                            >
                            <JetButton class="ml-3" @click="resetValueFilter(index)">Reset</JetButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </Dropdown>
              </p>
            </div>
          </template>
          <template #body="slotProps">
            <!-- Your normal cell content when there are Exits -->
            <div :class="currentMonth == value ? 'border-step-shadow' : ''" style="width: 100%; height: 40px">
              <div
                v-if="slotProps.value[valueArrayColumns['Exits']]"
                class="relative text-xs cursor-pointer text-center overflow-hidden overflow-x-scroll panel-custom-scrollbar w-full h-full flex items-center justify-center"
                :class="' ' + (index > cuurentMonthIndex ? 'text-blue-500' : '')"
                :data-testid="'underwriting-amount-' + value"
                @click="onUnderwritingControllerModal(value, slotProps.data, index)"
              >
                <p class="m-0 text-center">
                  {{ calculateAmount(slotProps.value) }}
                </p>
              </div>

              <!-- Fallback empty cell -->
              <div v-else style="width: 100%; height: 100%" class="h-24 w-20 bg-gray-100 relative"></div>
            </div>
          </template>
          <template #footer>
            <div
              :class="
                currentMonth == value
                  ? 'border-step-shadow'
                  : '' + ' ' + (index > cuurentMonthIndex ? 'text-blue-500' : '')
              "
              data-testid="total-amount"
              class="relative text-xs px-3 py-2 cursor-pointer text-center overflow-hidden overflow-x-scroll panel-custom-scrollbar"
              @click="onUnderwritingControllerModal(value, null, index)"
            >
              <p>
                {{ calculateAmount(totalTableData.value_array[index]) }}
              </p>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
    <div class="bg-white" style="height: 72px">
      <div class="flex flex-row justify-between items-center px-5 h-full">
        <div>
          <JetButton
            v-if="['GWP', 'GEP'].includes(currentAmountType)"
            class="left-0 text-gray-500 cursor-pointer -mt-1"
            link
            :disable-focus="true"
            @click="globalStore.setCurrencyModal(true)"
            ><i class="text-sm">
              Amounts in {{ globalStore.currency.currencyFormat.currency + gwpUnits[selectedGWPUnit][1] }}</i
            ></JetButton
          >
        </div>
        <div class="flex flex-row">
          <JetButton
            class="paginator-button-page-icon"
            :disable-focus="true"
            :disabled="underwritingStore.underwriting_metadata['page'] == 1"
            @click="changePage(1)"
          >
            <span>
              <FirstPageIcon class="paginator-icon" />
            </span>
          </JetButton>
          <JetButton
            class="paginator-button-page-icon"
            :disable-focus="true"
            :disabled="underwritingStore.underwriting_metadata['page'] == 1"
            @click="changePage(underwritingStore.underwriting_metadata['page'] - 1)"
          >
            <span>
              <PreviousPageIcon class="paginator-icon" />
            </span>
          </JetButton>
          <div class="flex items-center justify-center text-center ml-2 mr-2" style="color: rgb(113, 113, 122)">
            <p>
              Showing
              {{
                underwritingStore.underwriting_metadata['total_items'] === 0
                  ? 0
                  : underwritingStore.underwriting_metadata['per_page'] *
                      (underwritingStore.underwriting_metadata['page'] - 1) +
                    1
              }}
              to
              {{
                Math.min(
                  underwritingStore.underwriting_metadata['per_page'] * underwritingStore.underwriting_metadata['page'],
                  underwritingStore.underwriting_metadata['total_items']
                )
              }}
              of {{ underwritingStore.underwriting_metadata['total_items'] }}
            </p>
          </div>
          <JetButton
            class="paginator-button-page-icon"
            :disable-focus="true"
            :disabled="
              underwritingStore.underwriting_metadata['page'] == underwritingStore.underwriting_metadata['total_page']
            "
            @click="changePage(underwritingStore.underwriting_metadata['page'] + 1)"
          >
            <span>
              <NextPageIcon class="paginator-icon" />
            </span>
          </JetButton>
          <JetButton
            class="paginator-button-page-icon"
            :disabled="
              underwritingStore.underwriting_metadata['page'] == underwritingStore.underwriting_metadata['total_page']
            "
            :disable-focus="true"
            @click="changePage(underwritingStore.underwriting_metadata['total_page'])"
          >
            <span>
              <LastPageIcon class="paginator-icon" />
            </span>
          </JetButton>
        </div>
        <div class="flex flex-row">
          <Tooltip content="Clear table filters" placement="bottom" :show-after="1000">
            <JetButton
              class="flex items-center justify-center text-center"
              style="width: 32px; height: 32px"
              :style="{ backgroundColor: isFiltered == 0 ? ' rgb(200, 201, 204)' : 'rgb(230, 162, 60)' }"
              :disabled="isFiltered == 0"
              :disabled-focus="true"
              @click="resetAllFilters()"
            >
              <span class="flex items-center justify-center w-full h-full">
                <i class="pi pi-filter-slash"></i>
              </span>
            </JetButton>
          </Tooltip>
          <Tooltip content="Add new business" placement="bottom" :show-after="1000">
            <JetButton
              class="flex items-center justify-center text-center ml-3"
              :disabled-focus="true"
              style="width: 32px; height: 32px"
              @click="underwritingStore.isNewBusinessModal = true"
            >
              <span class="text-white">
                <PlusIcon class="paginator-button-icon" />
              </span>
            </JetButton>
          </Tooltip>
          <Tooltip content="Export table to CSV" placement="bottom" :show-after="1000">
            <JetButton
              class="flex items-center justify-center text-center ml-3"
              :disabled-focus="true"
              style="width: 32px; height: 32px"
              @click="openDownloadDialog()"
            >
              <span class="text-white">
                <DownloadIcon class="paginator-button-icon" />
              </span>
            </JetButton>
          </Tooltip>
          <Tooltip content="Signed Off Underwriting" placement="left" :show-after="1000">
            <JetButton
              class="flex items-center justify-center text-center bg-red-500 ml-3"
              style="width: 32px; height: 32px"
              :style="{ backgroundColor: portfolioStore.signedOff['underwriting_module'] ? '' : 'rgb(245, 108, 108)' }"
              :disable-focus="true"
              @click="portfolioStore.signedOffUnderwriting"
            >
              <span class="text-white">
                <EditPenIcon class="paginator-button-icon" />
              </span>
            </JetButton>
          </Tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.panel-custom-scrollbar::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome, Safari and Opera */
}

.panel-custom-scrollbar {
  -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
  scrollbar-width: none; /* Hide scrollbar for Firefox */
}

.internal-color {
  box-shadow: inset 0 0 10px #dddddd;
  background-color: rgb(250, 255, 255);
}

.custom-scroll::-webkit-scrollbar {
  width: 7px;
  height: 0px;
}

.custom-scroll::-webkit-scrollbar-track {
  background: #eeeeee;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: #888;
}

.custom-scroll::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.png-teal:hover {
  filter: invert(63%) sepia(42%) saturate(423%) hue-rotate(105deg) brightness(92%) contrast(93%);
}

.border-step-shadow {
  box-shadow: inset -10px 0 10px -5px #bbbbbb;
}

.dropdown-button {
  width: 14px !important;
  min-width: 14px !important;
  max-width: 14px !important;
  line-height: 14px !important;
  padding: 0 !important;
}

.paginator-button-icon {
  height: 15px;
  width: 15px;
}
.paginator-icon {
  height: 15px;
  width: 15px;
  color: #bbbbbb;
}
.paginator-button-page-icon {
  height: 46px;
  width: 46px;
  background-color: white;
  border: 1px solid #e5e7eb;
}
.paginator-button-page-icon:hover {
  background-color: rgb(244, 244, 245);
  border: 1px solid #e5e7eb;
}

.paginator-button-page-icon:hover .paginator-icon {
  color: rgb(113, 113, 122) !important;
}
</style>
